<!-- Live Stream editor page header + details card (Phase 3 editor template). -->

<template>
  <div class="lse-topbar">
    <header class="pe-header">
      <div class="pe-header__left">
        <div class="pe-header__title-row">
          <h1 class="bui-page-title">Live Stream Editor</h1>
          <button
            type="button"
            class="pe-header__refresh"
            aria-label="Reload live stream"
            :disabled="isLoading"
            @click="reloadPlayerSubscription()"
          >
            <PhArrowsClockwise :size="22" weight="bold" />
          </button>
        </div>
        <div class="pe-header__subtitle">{{ data.name }}</div>
      </div>
      <div class="pe-header__right">
        <button
          type="button"
          class="lse-stream-toggle"
          :class="{ 'lse-stream-toggle--stop': isLiveStreamStarted }"
          @click="toggleLiveStream"
        >
          <PhStop v-if="isLiveStreamStarted" :size="16" weight="bold" />
          <PhPlay v-else :size="16" weight="bold" />
          {{ isLiveStreamStarted ? 'Stop Stream' : 'Start Stream' }}
        </button>
      </div>
    </header>

    <InfoCard>
      <template #title>
        <h2 class="info-card__title-text">Stream Details</h2>
      </template>

      <div class="field-content">
        <div class="d-flex field-row">
          <label for="lse-name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
          <TextField id="lse-name" v-model="data.name" class="basis-1/2" width="100%" />
        </div>
        <div class="d-flex field-row field-row--top">
          <label for="lse-description" class="basis-1/2 text-sm font-bold">Description</label>
          <TextField id="lse-description" multiline v-model="data.description" class="basis-1/2" width="100%" />
        </div>
        <div class="d-flex field-row">
          <label class="basis-1/2 text-sm font-bold">Active</label>
          <div class="basis-1/2">
            <CheckBoxField
              v-model="data.isActive"
              name="active"
              :suffix-label="false"
              :label-with-margin="false"
            />
          </div>
        </div>
      </div>
    </InfoCard>
  </div>
</template>

<script lang="ts" setup>
import { PhArrowsClockwise, PhPlay, PhStop } from '@phosphor-icons/vue';
import TextField from '@/components/form-fields/TextField.vue';
import CheckBoxField from "@/components/form-fields/CheckboxField.vue";
import { useLiveStream } from '../composables/liveStream';
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';
import sharedRefs from '../sharedRefs';
import { computed } from 'vue';

const {
  data,
  reloadPlayerSubscription,
  isLoading
} = useLiveStream();

const { saveSubscriptionButtonRef, isStartStream } = sharedRefs;

const isLiveStreamStarted = computed(() => {
  return isStartStream.value;
});

function toggleLiveStream() {
  isStartStream.value = !isStartStream.value;
  saveSubscriptionButtonRef.value.saveLiveStream(isStartStream.value ? "start" : "stop");
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

.lse-topbar {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
}

/* Start (lemonlime CTA) / Stop (grape CTA) stream toggle in the header. */
.lse-stream-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1.25rem;
  height: var(--bui-cta-h, 52px);
  border: 0;
  border-radius: var(--bui-ter-radius, 8px);
  background: var(--bui-cta-pri-fill, #baff5c);
  color: var(--bui-cta-pri-text, #007366);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    filter: brightness(0.96);
  }

  &--stop {
    background: var(--bui-cta-sec-fill, #4b1886);
    color: var(--bui-cta-sec-text, #fffcfd);

    &:hover {
      filter: brightness(1.08);
    }
  }
}

/* Token-colour the checkbox field atom. */
.field-content {
  :deep(input[type='checkbox']) {
    accent-color: var(--bui-chk-checked-fill, #de3da9);
  }
}
</style>
